import React, { useState, useEffect, useCallback } from 'react';
import { Form, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faCubes, faTruck, faCalendarAlt, faClipboardList, faWarehouse,
  faCircleInfo, faTimes, faCheck,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { getKit, previewKitOrder, placeKitOrder } from '../services/api';
import '../styles/Cart.css';
import '../styles/modal-shared.css';

/*
 * Ordering kits. The server decides which lots fill the order and re-checks
 * everything when it is placed; this page shows the customer how the order
 * will ship before they commit. When a kit's components sit in more than one
 * warehouse the order ships as one pick order per warehouse, and those orders
 * are approved together.
 */

// Matches the cart checkout and the server's limit.
const INSTRUCTIONS_MAX = 200;
const PREVIEW_DELAY_MS = 350;

const fmtQty = (n) => Number(n || 0).toLocaleString();

export default function KitOrder() {
  const { kitId, tenantPrefix } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const link = useCallback((path) => (tenantPrefix ? `/${tenantPrefix}${path}` : path), [tenantPrefix]);

  const [kit, setKit] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [kitQty, setKitQty] = useState(String(Math.max(parseInt(searchParams.get('qty'), 10) || 1, 1)));
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [previewing, setPreviewing] = useState(false);

  const [shipTo, setShipTo] = useState({ name: '', address: '', city: '', state: '', zip: '', phone: '' });
  const [customerRef, setCustomerRef] = useState('');
  const [shipDate, setShipDate] = useState('');
  const [instructions, setInstructions] = useState('');
  const [validationError, setValidationError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const qty = parseInt(kitQty, 10);
  const qtyValid = Number.isInteger(qty) && qty > 0 && (!kit || qty <= kit.buildable);

  const loadKit = useCallback(async () => {
    try {
      setLoadError('');
      const loaded = await getKit(kitId);
      setKit(loaded);
      return loaded;
    } catch (err) {
      setLoadError(err.response?.status === 404 ? 'This kit was not found.' : 'Failed to load the kit. Please try again.');
      return null;
    }
  }, [kitId]);

  useEffect(() => { loadKit(); }, [loadKit]);

  // Preview how the order ships whenever the quantity settles.
  useEffect(() => {
    if (!kit || !qtyValid) { setPreview(null); return undefined; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setPreviewing(true);
      setPreviewError('');
      try {
        const result = await previewKitOrder(kitId, qty);
        if (!cancelled) setPreview(result);
      } catch (err) {
        if (!cancelled) {
          setPreview(null);
          setPreviewError(err.response?.data?.message || 'Could not work out how this order ships.');
        }
      } finally {
        if (!cancelled) setPreviewing(false);
      }
    }, PREVIEW_DELAY_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [kit, kitId, qty, qtyValid]);

  const showError = (message) => {
    setValidationError(message);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChange = (e) => setShipTo(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError('');
    if (!qtyValid) { showError(`Enter a number of kits from 1 to ${fmtQty(kit?.buildable)}`); return; }
    if (!shipDate) { showError('Please select an expected shipping date'); return; }
    if (instructions.length > INSTRUCTIONS_MAX) {
      showError(`Special Instructions is ${instructions.length} characters long, but the maximum is ${INSTRUCTIONS_MAX}.`);
      return;
    }

    setSubmitting(true);
    try {
      const result = await placeKitOrder(kitId, {
        kitQty: qty, shipDate, customerRef, instructions, shipTo,
      });
      const orders = result.orders?.length || 1;
      const what = orders > 1 ? ` (${orders} pick orders)` : '';
      if (result.requiresApproval) {
        toast.success(`Kit order submitted for approval${what}`, { duration: 6000 });
        window.dispatchEvent(new Event('refreshPendingCount'));
      } else {
        toast.success(`Kit order placed${what}`, { duration: 6000 });
      }
      navigate(link('/kits?tab=orders'));
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to place the kit order. Please try again.';
      showError(message);
      // Stock moved since the page loaded: show the current numbers.
      if (err.response?.status === 409) {
        const fresh = await loadKit();
        if (fresh && qty > fresh.buildable) setKitQty(String(Math.max(fresh.buildable, 1)));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div className="checkout-page"><div className="checkout-page-inner">
        <Alert variant="danger" className="mt-3">{loadError}</Alert>
        <button type="button" className="detail-btn detail-btn--ghost" onClick={() => navigate(link('/kits'))}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back to kits
        </button>
      </div></div>
    );
  }

  if (!kit) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">Loading kit…</p>
      </div>
    );
  }

  const parts = preview?.parts || [];
  const totalUnits = parts.reduce((n, p) => n + p.items.reduce((m, i) => m + i.qty, 0), 0);

  return (
    <div className="checkout-page">
      <div className="checkout-page-inner">
        <div className="checkout-page-header">
          <button className="checkout-back-btn" onClick={() => navigate(link('/kits'))} title="Back to kits">
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div>
            <h1 className="checkout-page-title">Order Kit: {kit.kitName}</h1>
            <p className="checkout-page-subtitle">
              {kit.kitDesc ? `${kit.kitDesc} · ` : ''}{fmtQty(kit.components.length)} components · {fmtQty(kit.buildable)} can be built now
            </p>
          </div>
        </div>

        {validationError && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: '0.82rem', color: '#dc2626', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FontAwesomeIcon icon={faCircleInfo} />
            {validationError}
          </div>
        )}

        {kit.buildable === 0 ? (
          <Alert variant="warning">
            None of this kit can be built from current stock. The Kits page shows which components are short.
          </Alert>
        ) : (
          <Form onSubmit={handleSubmit}>
            <div className="checkout-layout">
              {/* ── LEFT COLUMN ── */}
              <div>
                <div className="checkout-panel">
                  <div className="checkout-panel-header">
                    <div className="checkout-panel-icon"><FontAwesomeIcon icon={faCubes} /></div>
                    <h6>Kits</h6>
                  </div>
                  <div className="checkout-panel-body">
                    <div className="co-label">Number of kits <span style={{ color: '#ef4444' }}>*</span></div>
                    <Form.Control
                      size="sm" type="number" min={1} max={kit.buildable} step={1}
                      value={kitQty} onChange={(e) => setKitQty(e.target.value)}
                      isInvalid={!qtyValid} style={{ borderRadius: 8, maxWidth: 160 }}
                    />
                    <div style={{ fontSize: '0.72rem', color: qtyValid ? '#64748b' : '#dc2626', marginTop: 4 }}>
                      Up to {fmtQty(kit.buildable)} can be ordered from current stock.
                    </div>
                  </div>
                </div>

                <div className="checkout-panel">
                  <div className="checkout-panel-header">
                    <div className="checkout-panel-icon"><FontAwesomeIcon icon={faTruck} /></div>
                    <h6>Ship To <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '0.75rem' }}>(Optional)</span></h6>
                  </div>
                  <div className="checkout-panel-body">
                    <div className="row g-2">
                      <div className="col-12">
                        <div className="co-label">Name</div>
                        <Form.Control size="sm" type="text" name="name" value={shipTo.name} onChange={handleChange} maxLength={100} style={{ borderRadius: 8 }} />
                      </div>
                      <div className="col-12">
                        <div className="co-label">Street Address</div>
                        <Form.Control size="sm" type="text" name="address" value={shipTo.address} onChange={handleChange} maxLength={100} style={{ borderRadius: 8 }} />
                      </div>
                      <div className="col-5">
                        <div className="co-label">City</div>
                        <Form.Control size="sm" type="text" name="city" value={shipTo.city} onChange={handleChange} maxLength={45} style={{ borderRadius: 8 }} />
                      </div>
                      <div className="col-3">
                        <div className="co-label">State</div>
                        <Form.Control size="sm" type="text" name="state" value={shipTo.state} onChange={handleChange} maxLength={2} style={{ borderRadius: 8, textTransform: 'uppercase' }} />
                      </div>
                      <div className="col-4">
                        <div className="co-label">Zip</div>
                        <Form.Control size="sm" type="text" name="zip" value={shipTo.zip} onChange={handleChange} maxLength={10} style={{ borderRadius: 8 }} />
                      </div>
                      <div className="col-6">
                        <div className="co-label">Phone</div>
                        <Form.Control size="sm" type="text" name="phone" value={shipTo.phone} onChange={handleChange} maxLength={45} style={{ borderRadius: 8 }} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="checkout-panel">
                  <div className="checkout-panel-header">
                    <div className="checkout-panel-icon"><FontAwesomeIcon icon={faCalendarAlt} /></div>
                    <h6>Order Details</h6>
                  </div>
                  <div className="checkout-panel-body">
                    <div className="row g-2">
                      <div className="col-6">
                        <div className="co-label">Customer Reference</div>
                        <Form.Control size="sm" type="text" value={customerRef} onChange={(e) => setCustomerRef(e.target.value)} placeholder="Optional" maxLength={100} style={{ borderRadius: 8 }} />
                      </div>
                      <div className="col-6">
                        <div className="co-label">Expected Ship Date <span style={{ color: '#ef4444' }}>*</span></div>
                        <Form.Control
                          size="sm" type="date" value={shipDate} onChange={(e) => setShipDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]} required isInvalid={!shipDate} style={{ borderRadius: 8 }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="checkout-panel">
                  <div className="checkout-panel-header">
                    <div className="checkout-panel-icon"><FontAwesomeIcon icon={faClipboardList} /></div>
                    <h6>Shipping Method</h6>
                  </div>
                  <div className="checkout-panel-body">
                    <div style={{ background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 10, padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#16a34a' }} />
                        <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#15803d' }}>Hold for Instructions</span>
                      </div>
                      <div className="co-label" style={{ marginBottom: 4 }}>Special Instructions (Optional)</div>
                      <textarea
                        className="co-textarea" value={instructions} onChange={(e) => setInstructions(e.target.value)}
                        placeholder="Enter any special handling instructions, delivery notes, or hold conditions…"
                        rows={3} maxLength={INSTRUCTIONS_MAX}
                      />
                      <div style={{ marginTop: 4, fontSize: '0.72rem', textAlign: 'right', color: instructions.length >= INSTRUCTIONS_MAX ? '#dc2626' : '#64748b' }}>
                        {instructions.length} / {INSTRUCTIONS_MAX} characters
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── RIGHT COLUMN ── */}
              <div>
                <div className="checkout-panel">
                  <div className="checkout-panel-header">
                    <div className="checkout-panel-icon"><FontAwesomeIcon icon={faWarehouse} /></div>
                    <h6>How It Ships</h6>
                  </div>
                  <div className="checkout-panel-body">
                    {previewing && !preview && <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>}
                    {previewError && <div style={{ fontSize: '0.82rem', color: '#dc2626' }}>{previewError}</div>}
                    {!qtyValid && !previewError && (
                      <div style={{ fontSize: '0.82rem', color: '#64748b' }}>Enter a number of kits to see how the order ships.</div>
                    )}
                    {preview && (
                      <>
                        {parts.length > 1 && (
                          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 12px', fontSize: '0.78rem', color: '#1e40af', marginBottom: 10 }}>
                            This kit&apos;s components are held in {parts.length} warehouses, so the order ships as
                            {' '}{parts.length} pick orders, one from each. They are approved together.
                          </div>
                        )}
                        <div style={{ maxHeight: 420, overflowY: 'auto', opacity: previewing ? 0.6 : 1 }}>
                          {parts.map((part, i) => (
                            <div key={part.idWarehouse} style={{ marginBottom: 10 }}>
                              <div className="checkout-warehouse-name" style={{ marginBottom: 4 }}>
                                {parts.length > 1 ? `${i + 1}. ` : ''}From {part.warehouseName}
                              </div>
                              {part.items.map(item => (
                                <div key={item.idCatalog} className="co-summary-item">
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className="co-summary-item-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.itemName || item.itemNumber}</div>
                                    <div className="co-summary-item-lot">{item.itemNumber}{item.lots.length > 1 ? ` · ${item.lots.length} lots` : ''}</div>
                                  </div>
                                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 8 }}>
                                    <div className="co-summary-item-qty">{fmtQty(item.qty)} {item.unit || 'units'}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                        <div className="co-total-row">
                          <span className="co-total-label">{fmtQty(qty)} kit{qty !== 1 ? 's' : ''} · {fmtQty(totalUnits)} units</span>
                          <span className="co-total-value">{parts.length} pick order{parts.length !== 1 ? 's' : ''}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="checkout-footer">
                  <button type="button" className="detail-btn detail-btn--ghost" onClick={() => navigate(link('/kits'))}>
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                  </button>
                  <button type="submit" className="detail-btn detail-btn--primary" disabled={submitting || !qtyValid || !preview}>
                    {submitting
                      ? <><Spinner animation="border" size="sm" /> Placing…</>
                      : <><FontAwesomeIcon icon={faCheck} /> Place Kit Order</>}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        )}
      </div>
    </div>
  );
}
